import Head from "next/head"
import Footer from "components/footer"
import dynamic from "next/dynamic"
// Dynamic import to avoid SSR and hydration errors
const Header = dynamic(() => import("components/header"), { ssr: false })
import { useContext } from "react"
import { styledButton2 } from "components/elements"
import { CityContext } from "app/features/regional-map/regional-map.state"
import DownloadOptionsDropdown from "components/download_options_dropdown"
import { updateText } from "app/utils/formatting_utils"
import { Routes } from "@blitzjs/next"
import Link from "next/link"
import { STATIC_ASSETS_CLOUDFRONT_URL } from "app/constants"

function Benefit({ src, children }: { src: string; children: React.ReactNode }) {
  return (
    <div className="align-center flex h-36 flex-col justify-center rounded-xl border-2 border-solid border-gray-100 bg-white p-2 text-center">
      <img alt="" src={src} className="mx-auto mb-4 max-h-12 max-w-12" />
      <div className="text-xs font-bold uppercase leading-tight tracking-wider text-gray-600">
        {children}
      </div>
    </div>
  )
}

export const LocationLandingPage = () => {
  const city = useContext(CityContext)!

  return (
    <div>
      <Head>
        <title>TESA {city?.shortTitle || ""}</title>
        <link rel="icon" href="/favicon.ico" />
        <meta
          name="description"
          content="Find your score and help create Tree Equity in cities and towns across America."
        />
      </Head>
      <div
        className="mx-0 flex h-screen flex-col lg:mx-4 lg:h-[98vh]"
        style={{
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "left center",
          backgroundImage: `url(${
            city.landingPageBackground ? city.landingPageBackground : "/trees-home.jpg"
          })`,
        }}
      >
        <div className="bg-white">
          <Header />
        </div>
        <div className="flex h-full flex-row items-center pl-[10%]">
          <div className="w-11/12 space-y-1 rounded-xl bg-white bg-opacity-[90%] p-6 text-gray-700 lg:w-3/4 lg:p-10 xl:w-[50%] xl:space-y-4">
            <h1 className="text-3xl font-medium leading-tight lg:text-4xl xl:text-5xl">
              Tree Equity Score Analyzer
            </h1>
            <h1 className="text-2xl font-bold leading-tight text-brand-green-dark lg:text-3xl">
              {city.title}
            </h1>

            <p className="pt-2 text-lg leading-snug md:text-xl">
              Discover how human-centered tree plantings can improve health and well-being in{" "}
              {city.shortTitle}'s {updateText("urban", city)} {updateText("neighborhood", city)}s.
            </p>
            <p className="pb-3 pt-1 text-base leading-normal lg:text-lg xl:pt-2 xl:text-xl">
              Deep dive into the {city.shortTitle} Tree Equity Score Analyzer for{" "}
              {updateText("neighborhood", city)}- and property-level tools that can help you chart a
              course toward Tree Equity.
            </p>
            <div>
              <Link
                href={Routes.Map({ city: city.id })}
                className={`${styledButton2({
                  variant: "primary",
                  size: "sm",
                })} text-base sm:text-lg`}
              >
                Launch the app
              </Link>
            </div>
          </div>
        </div>

        {city.photoCredit && (
          <div className="group">
            <div className="absolute bottom-[4%] left-[2%] z-[1000] h-6 w-6 rounded-full bg-black text-center font-bold text-white opacity-75 group-hover:cursor-pointer group-hover:bg-[#33966D] group-hover:duration-1000">
              i
            </div>
            <div className="z-1 absolute bottom-[4%] left-[3%] ml-[20px] w-fit whitespace-nowrap rounded bg-black p-[5px] text-center text-xs font-medium text-white opacity-0 duration-1000 group-hover:opacity-100">
              Image: {city.photoCredit}
            </div>
          </div>
        )}
      </div>
      <div className="pt-32">
        <div className="grid gap-x-10 md:grid-cols-2">
          <div className="pl-[5%] pr-[5%] md:pl-[10%] md:pr-0 lg:pl-[20%]">
            <h2 className="pt-0 text-4xl font-medium leading-tight text-brand-green-dark xl:pt-[15%]">
              <span className="text-gray-700">Tree Equity </span>for {city.shortTitle}
            </h2>
            <p className="mt-4 text-xl leading-relaxed text-gray-700">
              Tree Equity is about prioritizing areas with the greatest need to fill gaps in tree
              cover, so that {city.shortTitle} residents can experience all the health, economic and
              other benefits that trees provide.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-gray-700 md:text-xl">
              The {city.shortTitle} Tree Equity Score Analyzer (TESA) is your free, map-based
              application to help you create a roadmap to shift your Tree Equity Scores.
            </p>
          </div>
          <div className="flex items-center py-4">
            <img alt="" loading="lazy" src="/TESA_LandingPage_LaptopCrop_md.png" />
          </div>
        </div>

        <div
          className="bg-white pb-20 pt-40"
          style={{
            backgroundImage: "url(/tes-footer-gradient.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "top center",
            backgroundRepeat: "no-repeat",
            imageRendering: "crisp-edges",
          }}
        >
          <h3 className="m-auto w-11/12 pt-4 text-center text-2xl font-bold text-gray-700 lg:w-1/2">
            Tree Equity Score evaluates data for all {updateText("urban", city)} areas in{" "}
            {city.shortTitle} to help prioritize areas with the greatest need.
          </h3>
          <div className="mx-16 grid max-w-5xl grid-cols-2 gap-x-4 gap-y-5 py-10 sm:grid-cols-4 lg:mx-auto lg:grid-cols-8">
            <Benefit src="/icons/tree.svg">Tree canopy</Benefit>
            <Benefit src="/icons/buildingdensity.svg">Building density</Benefit>
            <Benefit src="/icons/income.svg">Income & Employment</Benefit>
            <Benefit src="/icons/race.svg">Race</Benefit>
            <Benefit src="/icons/temp.svg">Surface temperature</Benefit>
            <Benefit src="/icons/health.svg">Health</Benefit>
            <Benefit src="/icons/language.svg">Language</Benefit>
            <Benefit src="/icons/age.svg">Age</Benefit>
          </div>
        </div>

        {/* <Link href={Routes.Map({ city: city.id })} className={`${styledButton2({ variant: "primary", size: "md" })} text-xl`}>
                Launch the app
            </Link> */}
      </div>
      <div className="bg-gradient-to-b from-white to-[#ECF5F2] py-40">
        <div className="mx-auto max-w-5xl lg:px-0">
          <h4 className="pb-4 text-center text-4xl font-medium text-brand-green-dark">
            How TESA works
          </h4>
          <p className="pb-6 text-center text-xl text-gray-700">
            Use Tree Equity Score and supplementary local data to identify{" "}
            {updateText("neighborhood", city)}s with the greatest need. Then dive deeper to the
            property level to discover how you can shift Tree Equity Scores through tree planting.
          </p>
          <div className="grid gap-x-8 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4 md:p-0">
            <div className="flex flex-col items-center rounded-lg bg-white p-8 ring-1 ring-gray-200">
              <div className="pb-4 text-center">
                Use our interactive tools and data to explore your location.
              </div>
              <img src="/step1.svg" alt="" />
            </div>
            <div className="flex flex-col items-center rounded-lg bg-white p-8 ring-1 ring-gray-200">
              <div className="pb-4 text-center">
                Find out where to plant trees to create the biggest impact.
              </div>
              <img src="/step2.svg" alt="" />
            </div>
            <div className="flex flex-col items-center rounded-lg bg-white p-8 ring-1 ring-gray-200">
              <div className="pb-4 text-center">
                Create, save and share your tree planting scenario.
              </div>
              <img src="/step3.svg" alt="" />
            </div>
            <div className="flex flex-col items-center rounded-lg bg-white p-8 ring-1 ring-gray-200">
              <div className="pb-4 text-center">
                Use your findings to implement change in your community.
              </div>
              <img src="/step4.svg" alt="" />
            </div>
          </div>
          {/* additional methods & glossary */}
          {(city.pdfLinks && Object.keys(city.pdfLinks).length > 0) ||
          (city.locale && city.locale !== "en-US") ? (
            <div className="mt-8 flex flex-wrap justify-center gap-4 text-center">
              {city.pdfLinks &&
                Object.keys(city.pdfLinks).length > 0 &&
                Object.entries(city.pdfLinks).map(([title, url]) => (
                  <a
                    key={title}
                    className="rounded-full bg-brand-green px-6 py-2 text-base text-white transition-all duration-500 hover:bg-brand-green-dark sm:text-xl"
                    href={url}
                    target="blank_"
                    rel="noreferrer noopener"
                  >
                    {title}
                  </a>
                ))}
              {city.locale && city.locale !== "en-US" && (
                <div className="-ml-6">
                  <DownloadOptionsDropdown
                    label={city.shortTitle}
                    s3key={`tesa_${city.id}`}
                    locale={city.locale}
                  />
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
      {city.resourcesLinks && Object.keys(city.resourcesLinks).length > 0 ? (
        <div id="resources" className="bg-gradient-to-b from-white to-[#ECF5F2] py-40">
          <div className="mx-auto max-w-5xl lg:px-0">
            <h4 className="pb-4 text-center text-4xl font-medium text-brand-green-dark">
              Help protect and restore urban forests in {city.shortTitle}
            </h4>
            <p className="pb-6 text-center text-xl text-gray-700">
              Find local resources to guide your efforts to plant and protect a healthy urban canopy
              in {city.shortTitle} and achieve Tree Equity.
            </p>
          </div>
          <div className="mx-auto px-[20%] lg:px-[5%]">
            {city.resourcesLinks && Object.keys(city.resourcesLinks).length > 0 && (
              <>
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 pt-1 sm:grid-cols-2 lg:grid-cols-4">
                  {Object.entries(city.resourcesLinks).map(([title, url]) => (
                    <a
                      key={title}
                      className="flex min-h-[100px] items-center gap-3 rounded-lg border-2 border-brand-green bg-white p-4 text-lg font-medium text-brand-green-dark shadow-xl transition-all duration-500 hover:bg-gray-200"
                      href={url}
                      target="blank_"
                      rel="noreferrer noopener"
                    >
                      <img
                        src={`${STATIC_ASSETS_CLOUDFRONT_URL}/resources-ln.png`}
                        alt=""
                        className="h-12 w-12 flex-shrink-0"
                      />
                      <span className="text-left leading-snug">{title}</span>
                    </a>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
      <div className="bg-gradient-to-t from-white to-[#ECF5F2] py-5 lg:py-20">
        <div className="grid-col-1 m-auto grid w-4/5 gap-x-20 lg:grid-cols-2">
          <div className="space-y-4 py-2">
            <p className="text-3xl font-medium text-brand-green-dark">About the project</p>
            <p className="text-lg leading-normal text-gray-700 md:text-xl md:leading-normal">
              The {city.shortTitle} Tree Equity Score Analyzer was launched in{" "}
              {city.releaseDate ? city.releaseDate : "Spring 2022"}. Every TESA is customized to
              support local decision-making needs. American Forests co-developed this tool with a
              team of dedicated {city.shortTitle} stakeholders composed of city{" "}
              {updateText("and state ", city)}
              government, non-profit environmental organizations and residents. The supplementary
              data incorporated into {city.shortTitle}'s TESA&mdash;for example,{" "}
              {city.supplementaryData}&mdash;represent factors that are most important to
              stakeholder council members and {city.shortTitle} residents when they make decisions
              about planting and protecting trees.
            </p>
            <p className="text-3xl font-medium text-brand-green-dark">Our Funders</p>
            <div className="flex flex-wrap text-base text-white sm:text-xl">
              {/* the following empty p tag prevents the first button from stretching vertically */}
              <p></p>
              <a
                className="mr-4 rounded-full bg-brand-orange-light px-4 py-1 transition-all duration-500 hover:bg-brand-orange"
                href={city.funderLink}
                target="blank_"
                rel="noreferrer noopener"
              >
                {city.funder}
              </a>
            </div>
          </div>
          <div className="space-y-4 py-6 lg:py-2">
            <p className="text-3xl font-medium text-brand-green-dark">Our Partners</p>
            <div className="pt-1">
              <a
                className="rounded-full bg-brand-green px-4 py-1 text-base text-white transition-all duration-500 hover:bg-brand-green-dark sm:text-xl"
                href={city.PrimaryPartnerLink}
                target="blank_"
                rel="noreferrer noopener"
              >
                {city.PrimaryPartner}
              </a>
              <p className="pt-5 text-lg leading-relaxed text-gray-700 md:text-xl md:leading-relaxed">
                {city.partnerList}
              </p>
            </div>
            {/* Keeping this here in case we want to add links under partners in the future */}
            {/* {city.resourcesLinks && Object.keys(city.resourcesLinks).length > 0 && (
              <>
                <p className="text-3xl font-medium text-brand-green-dark">Resources</p>
                <div className="pt-1 flex flex-wrap gap-2">
                  {Object.entries(city.resourcesLinks).map(([title, url]) => (
                    <a
                      key={title}
                      className="sm:text-xl text-base text-white bg-brand-green hover:bg-brand-green-dark transition-all duration-500 rounded-full py-1 px-4 mb-2"
                      href={url}
                      target="blank_"
                      rel="noreferrer noopener"
                    >
                      {title}
                    </a>
                  ))}
                </div>
              </>
            )} */}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}
